import { messages } from '../../config/messages';

interface BacDisplayProps {
  currentBac: number;
  targetBac: number;
  trend: 'rising' | 'falling' | 'stable';
}

export default function BacDisplay({ currentBac, targetBac, trend }: BacDisplayProps) {
  // Determine status color
  const getStatusColor = () => {
    if (currentBac <= 0) return 'text-gray-400';
    if (currentBac < targetBac * 0.8) return 'text-green-400';
    if (currentBac <= targetBac) return 'text-yellow-400';
    if (currentBac <= targetBac * 1.2) return 'text-orange-400';
    return 'text-red-400';
  };

  // Trend arrow
  const getTrendIcon = () => {
    switch (trend) {
      case 'rising':
        return '↑';
      case 'falling':
        return '↓';
      case 'stable':
        return '→';
    }
  };

  const getTrendColor = () => {
    if (trend === 'stable') {
      return 'text-gray-400';
    }

    const isApproachingTarget =
      (currentBac < targetBac && trend === 'rising') ||
      (currentBac > targetBac && trend === 'falling');

    return isApproachingTarget ? 'text-green-400' : 'text-red-400';
  };

  const getTrendStyle = (): React.CSSProperties => {
    if (trend === 'stable') return {};

    const keyframes = trend === 'rising'
      ? 'trendBounceUp 1.2s ease-in-out infinite'
      : 'trendBounceDown 1.2s ease-in-out infinite';

    return { animation: keyframes };
  };

  // Status text
  const getStatusText = () => {
    if (currentBac <= 0) return messages.status.sober;
    if (currentBac < targetBac * 0.5) return messages.status.warming;
    if (currentBac < targetBac) return messages.status.approaching;
    if (currentBac <= targetBac * 1.1) return messages.status.onTarget;
    return messages.status.overTarget;
  };

  return (
    <div className="text-center space-y-2">
      {/* Main BAC display */}
      <div className="flex items-center justify-center gap-2">
        <span className={`text-6xl font-bold ${getStatusColor()} transition-colors`}>
          {currentBac.toFixed(2)}
        </span>
        <span className="text-2xl text-gray-400">‰</span>
        <span
          className={`text-3xl inline-block ${getTrendColor()}`}
          style={getTrendStyle()}
        >
          {getTrendIcon()}
        </span>
      </div>

      {/* Target info */}
      <div className="text-gray-400">
        Cíl: <span className="text-white font-medium">{targetBac.toFixed(1)} ‰</span>
      </div>

      {/* Status text */}
      <div className={`text-lg font-medium ${getStatusColor()}`}>
        {getStatusText()}
      </div>
    </div>
  );
}
